// KYT v2 · WALLET COVERAGE (Astra's `kyt-wallets`) — A HELD OPTION, NOT IN USE. The operator's N-036 (4) — "one wallet
// list on /kyt Wallets instead of three sections" — is open with the default "No". This is drawn so he can see it, at
// /kyt?view=coverage only: no tab, no link and no default lands on it; today's Wallets view (the lane's three sections)
// stands. One register of the desk's wallets — the Books' treasury register, this month's desk wallets, and any wallet the
// screener watches that the Books do not know — each with its watch state, whether its payments are read, and what the
// screener last said of it (information, no green). A row opens the lane's own wallet trace.
import { useMemo, useState } from 'react';
import type { KytBooksWallet, KytIncoming, KytIncomingWallet, KytOverview, KytWatchedWallet } from '../../../lib/api';
import { fmtDay, fmtRelative } from '../../../lib/format';
import { partyName } from '../../../lib/naming';
import { WalletTrace, network, paymentsUnread, readerTrouble, sameWallet, traceInfo } from '../../../screens/Kyt';
import { ChevronRight } from 'lucide-react';
import { EmptyState, Notice, Panel, Register, SearchBar, StateLine, type RegisterColumn } from '../kit';

interface Row {
  key: string; chain: string; address: string; name: string; sub: string;
  treasury: boolean; retired: boolean;
  books?: KytBooksWallet; desk?: KytIncomingWallet; watched?: KytWatchedWallet;
}

function rowsOf(d: KytOverview | undefined, inc: KytIncoming | undefined): Row[] {
  const out: Row[] = [];
  const books = (d?.books_wallets ?? []).filter((w) => w.party_type === 'own_account');
  for (const b of books) {
    const name = b.label ?? partyName({ display_name: b.display_name, slug: b.slug });
    out.push({
      key: `b:${b.chain}:${b.address}`, chain: b.chain, address: b.address, name, treasury: true, retired: b.status === 'archived',
      sub: b.status === 'archived' ? `Retired${b.retired_at ? ` ${fmtDay(b.retired_at, true)}` : ''} · still paid into` : 'Treasury',
      books: b, desk: inc?.wallets.find((w) => sameWallet(w, b)), watched: d?.wallets.find((w) => sameWallet(w, b)),
    });
  }
  // This month's desk wallets the register does not hold, then watched wallets nobody registered.
  for (const w of inc?.wallets ?? []) {
    if (out.some((r) => sameWallet(r, w))) continue;
    out.push({ key: `d:${w.chain}:${w.address}`, chain: w.chain, address: w.address, name: w.label ?? `${w.chain} wallet`, sub: 'Desk wallet · not in the Books',
      treasury: true, retired: false, desk: w, watched: d?.wallets.find((x) => sameWallet(x, w)) });
  }
  for (const w of d?.wallets ?? []) {
    if (out.some((r) => sameWallet(r, w))) continue;
    out.push({ key: `w:${w.chain}:${w.address}`, chain: w.chain, address: w.address, name: w.label ?? `${w.chain} wallet`, sub: 'Watched · not in the Books',
      treasury: false, retired: false, watched: w });
  }
  return out;
}

/** Watch state and whether its payments are read, in two short lines. Not watched is amber: nobody screens it. */
function CoverageCell({ r }: { r: Row }) {
  const watched = r.books ? r.books.watched : !!r.watched;
  const trouble = r.desk ? readerTrouble(r.desk)[0] : undefined;
  const read = !r.desk ? null
    : !r.desk.read ? { text: 'Payments not read', tone: 'text-warn' }
      : paymentsUnread(r.desk) ? { text: trouble?.tone === 'bad' ? 'Reader failing' : 'Read in part', tone: trouble?.tone === 'bad' ? 'text-neg' : 'text-warn' }
        : { text: 'Payments read', tone: 'text-tg-hint' };
  return (
    <span className="block">
      <span className={`block ${watched ? '' : 'text-warn'}`}>{watched ? 'Watched' : 'Not watched'}</span>
      {read && <span className={`block text-meta ${read.tone}`}>{read.text}</span>}
    </span>
  );
}

/** What the screener last said of the wallet itself, and when — a score is information, never a clean bill. */
function EvidenceCell({ r }: { r: Row }) {
  const gl = r.desk?.gl;
  if (gl) {
    return (
      <span className="block">
        <span className="block">GL {Math.round(gl.score)} · {gl.band}</span>
        <span className={`block text-meta ${gl.thin ? 'text-warn' : 'text-tg-hint'}`}>{gl.thin ? 'little data' : gl.at ? `scored ${fmtRelative(gl.at)}` : 'not dated'}</span>
      </span>
    );
  }
  const band = r.books?.band ?? r.watched?.band ?? null;
  const at = r.books?.last_checked ?? r.watched?.lastChecked ?? null;
  if (band) {
    return (
      <span className="block">
        <span className={`block ${band === 'high' || band === 'severe' ? 'text-warn' : ''}`}>Band {band}</span>
        <span className="block text-meta text-tg-hint">{at ? `checked ${fmtRelative(at)}` : 'not dated'}</span>
      </span>
    );
  }
  return <span className="block text-tg-hint">{at ? 'No score' : 'Not scored'}</span>;
}

const Chevron = () => <ChevronRight className="h-4 w-4 shrink-0 text-tg-hint" aria-hidden />;

const COLUMNS: ReadonlyArray<RegisterColumn<Row>> = [
  { key: 'wallet', head: 'Wallet', cell: (r) => (
    <span className="block min-w-0"><span className="block break-words font-medium">{r.name}</span><span className="block text-meta text-tg-hint">{r.sub}</span></span>
  ) },
  { key: 'network', head: 'Network', width: 'w-[12%]', cell: (r) => <span className="font-mono text-meta">{network(r.chain)}</span> },
  { key: 'coverage', head: 'Coverage', width: 'w-[20%]', cell: (r) => <CoverageCell r={r} /> },
  { key: 'evidence', head: 'Evidence', width: 'w-[20%]', cell: (r) => <EvidenceCell r={r} /> },
  { key: 'open', head: '', width: 'w-[56px]', align: 'right', cell: () => <span className="inline-flex justify-end"><Chevron /></span> },
];

const card = (r: Row) => ({
  title: <><span className="block">{r.name}</span><span className="block text-meta font-normal text-tg-hint">{r.sub} · {network(r.chain)}</span></>,
  figure: <Chevron />,
  meta: <EvidenceCell r={r} />,
  state: <CoverageCell r={r} />,
});

export function CoverageView({ d, inc, overviewFailed, onRetry }: { d: KytOverview | undefined; inc: KytIncoming | undefined; overviewFailed: boolean; onRetry: () => void }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Row | null>(null);
  const rows = useMemo(() => rowsOf(d, inc), [d, inc]);
  const needle = q.trim().toLowerCase();
  const shown = needle ? rows.filter((r) => `${r.name} ${r.sub} ${r.address} ${network(r.chain)}`.toLowerCase().includes(needle)) : rows;
  const budgetOut = d ? d.spend.arkhamToday >= d.spend.arkhamBudget : false;
  return (
    <>
      <Notice tone="neutral">Held option, N-036 (4) — not in use.</Notice>
      {overviewFailed && <Notice tone="bad" action={<button type="button" className="btn-link text-body" onClick={onRetry}>Try again</button>}>Wallets not read.</Notice>}
      {d?.books_error && <StateLine tone="warn" className="mb-4">Register not read — the Books did not answer.</StateLine>}
      <SearchBar value={q} onChange={setQ} placeholder="Search wallet, network or address" />
      {d && (
        <Panel flush>
          {shown.length === 0
            ? <EmptyState>{needle ? 'No wallet matches.' : 'No wallet registered or watched.'}</EmptyState>
            : <Register label="Wallets" rows={shown} rowKey={(r) => r.key} onOpen={setOpen} columns={COLUMNS} card={card} />}
          <div className="flex items-center justify-between gap-4 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span>{shown.length} wallet{shown.length === 1 ? '' : 's'} · Books register and watchlist</span>
            <span>Dubai time</span>
          </div>
        </Panel>
      )}
      {open && d && (
        <WalletTrace chain={open.chain} address={open.address} desk={open.desk} watched={open.watched} books={open.books}
          trace={traceInfo(d, budgetOut)} onClose={() => setOpen(null)} />
      )}
    </>
  );
}
